import _ from 'lodash';
import {Key} from 'react';
import {createSelector} from 'reselect';

import {
  VisualBasicCollapsedActivity,
  VisualBasicCollapsedActivityTypesEnum,
} from '../../../../srcCommon/inboxes/viewer/activity/visualActivity/visualBasicCollapsedActivity';
import {
  ActivityModel,
  ActivityVisualTypesEnum,
  BasicActivityModel,
  BasicActivityTypesEnum,
  BasicVisualTypesEnum,
} from '../../../models/api/activityModel';
import {
  ActivitySourceTypesEnum,
  ActivitySourceVisualTypesEnum,
} from '../../../models/api/activitySourceModel';
import {InboxModel} from '../../../models/api/inboxModel';
import {AppState} from '../../appState';
import {ResourceMap} from '../../appTypes';
import {ActivityViewerState} from '../../panels/viewer/activity/activityViewerState';
import {getInboxes} from '../inboxes/inboxesSelectors';
import {FindTeammate, getFindTeammate} from '../teammates/lazyTeammatesSelectors';
import {getUserId} from '../teammates/teammatesSelectors';

/*
 * Props.
 */

export interface BasicViewerGroupHeaderContainerProps {
  activities: ReadonlyArray<ActivityModel>;
  activityIndex: number;
  focusKey: Key | undefined;
}

export interface BasicViewerContainerInheritableProps extends BasicViewerGroupHeaderContainerProps {
  activityStates: ResourceMap<ActivityViewerState>;
}

/*
 * Selectors.
 */

function getActivities(_state: AppState, props: BasicViewerGroupHeaderContainerProps) {
  return props.activities;
}

function getActivityIndex(_state: AppState, props: BasicViewerGroupHeaderContainerProps) {
  return props.activityIndex;
}

function getFocusKey(_state: AppState, props: BasicViewerGroupHeaderContainerProps) {
  return props.focusKey;
}

function getActivityStates(_state: AppState, props: BasicViewerContainerInheritableProps) {
  return props.activityStates;
}

export function makeGetBasicActivityGroupState() {
  return createSelector(
    [getActivities, getActivityStates, getActivityIndex, makeGetFindCanActivityBeCollapsed()],
    (activities, activityStates, activityIndex, findCanActivityBeCollapsed) => {
      // If this activity is not collapsible, early return.
      if (!findCanActivityBeCollapsed(activityIndex)) {
        return undefined;
      }

      const collapsibleActivitiesBeforeCount = findCollapsibleActivitiesBeforeCount(
        findCanActivityBeCollapsed,
        activityIndex,
      );

      // Find the main activity of this collapse group.
      const collapseGroupActivity = activities[activityIndex - collapsibleActivitiesBeforeCount];
      if (!collapseGroupActivity) {
        return undefined;
      }

      // And its corresponding state.
      const collapseGroupActivityState = activityStates[collapseGroupActivity.id];
      if (!collapseGroupActivityState || collapseGroupActivityState.type !== ActivityVisualTypesEnum.BASIC) {
        return undefined;
      }

      return collapseGroupActivityState;
    },
  );
}

function makeGetFindCanActivityBeCollapsed() {
  return createSelector(
    [getUserId, getFindTeammate, getInboxes, getActivities, getFocusKey],
    (userId, findTeammate, inboxes, activities, focusKey) => (activityIndex: number) => {
      // Only basic activities can be collapsed.
      const activity = activities[activityIndex];
      if (!activity || activity.visualType !== ActivityVisualTypesEnum.BASIC) {
        return false;
      }

      // Check if the individual activity meets the conditions to be collapsed.
      if (!isBasicActivityCollapsible(userId, findTeammate, inboxes, activity)) {
        return false;
      }

      // If this is the activity that we'd like to focus, it needs to be visible.
      if (activity.id === focusKey) {
        return false;
      }

      // Sending Errors should never be collapsed.
      return (
        activity.type !== BasicActivityTypesEnum.SENDING_ERROR &&
        activity.type !== BasicActivityTypesEnum.MESSAGE_BOUNCE_ERROR
      );
    },
  );
}

function findCollapsibleActivitiesBeforeCount(
  findCanActivityBeCollapsed: (index: number) => boolean,
  activityIndex: number,
) {
  let index = activityIndex;
  let count = 0;

  while (findCanActivityBeCollapsed(--index)) {
    count++;
  }

  return count;
}

export function makeGetGroupHeaderActivities() {
  return createSelector(
    [getUserId, getFindTeammate, getInboxes, makeGetCollapsibleActivitiesAfter()],
    (userId, findTeammate, inboxes, groupActivities) => {
      // The tagged and untagged texts include a count of other activities of the same type, and have a special text.
      const tagAddedCount = groupActivities.filter((a) => a.type === BasicActivityTypesEnum.TAG).length;
      const removedTagCount = groupActivities.filter((a) => a.type === BasicActivityTypesEnum.UNTAG).length;

      return _(groupActivities)
        .map((activity) =>
          buildBasicCollapsedActivity(
            userId,
            findTeammate,
            inboxes,
            activity,
            tagAddedCount,
            removedTagCount,
          ),
        )
        .compact()
        .value();
    },
  );
}

// Return a sequence of collapsible activities (collapse group) starting at the given index.
function makeGetCollapsibleActivitiesAfter() {
  return createSelector(
    [getActivities, getActivityIndex, makeGetFindCanActivityBeCollapsed()],
    (activities, activityIndex, findCanActivityBeCollapsed) => {
      const collapsibleActivities: Array<BasicActivityModel> = [];
      let index = activityIndex;

      // A group of activities are adjacent activities which can be collapsed together.
      while (findCanActivityBeCollapsed(index)) {
        const activity = activities[index];
        if (!activity || activity.visualType !== ActivityVisualTypesEnum.BASIC) {
          break;
        }

        collapsibleActivities.push(activity);
        index += 1;
      }

      return collapsibleActivities;
    },
  );
}

/* Check if an activity has a medium priority, or if a collapsed state cannot be computed from the activity. */
export function isBasicActivityCollapsible(
  userId: number,
  findTeammate: FindTeammate,
  inboxes: ResourceMap<InboxModel>,
  activity: BasicActivityModel,
) {
  // If this activity meets the conditions of a medium-priority item, it cannot be collapsed.
  if (isActivityMediumPriority(activity)) {
    return false;
  }

  // If this activity's collapsed state cannot be computed, we should not include it in the group header.
  if (!canBuildBasicCollapsedActivity(userId, findTeammate, inboxes, activity)) {
    return false;
  }

  // NOTE: The "Snooze again" medium priority activity always shows-up as it is a non-basic activity,
  // and is handled in messageViewer.tsx rather than basicViewer.tsx.
  return true;
}

export function isActivityMediumPriority(activity: BasicActivityModel) {
  // Error for sending a message.
  if (
    activity.visualType === ActivityVisualTypesEnum.BASIC &&
    activity.basicVisualType === BasicVisualTypesEnum.ERROR
  ) {
    return true;
  }

  // Integration topic updated (JIRA, GitHub, etc.)
  if (
    activity.visualType === ActivityVisualTypesEnum.BASIC &&
    activity.basicVisualType === BasicVisualTypesEnum.TOPIC
  ) {
    return true;
  }

  // Conversation split because a message was moved to a new conversation.
  if (activity.type === BasicActivityTypesEnum.SPLIT_ORIGIN) {
    return true;
  }

  // Conversation was created by splitting another conversation.  */
  if (activity.type === BasicActivityTypesEnum.SPLIT_TARGET) {
    return true;
  }

  // Conversation manually merged.
  if (activity.type === BasicActivityTypesEnum.MERGE) {
    return true;
  }

  return false;
}

export function canBuildBasicCollapsedActivity(
  userId: number,
  findTeammate: FindTeammate,
  inboxes: ResourceMap<InboxModel>,
  activity: BasicActivityModel,
) {
  return Boolean(buildBasicCollapsedActivity(userId, findTeammate, inboxes, activity, 0, 0));
}

/*
 * Compute the collapsed activity details, with checks in order of priority.
 * There are currently three levels of priority, and otherwise sorting should be chronological.
 */
function buildBasicCollapsedActivity(
  userId: number,
  findTeammate: FindTeammate,
  inboxes: ResourceMap<InboxModel>,
  activity: BasicActivityModel,
  addedTagCount: number,
  removedTagCount: number,
): VisualBasicCollapsedActivity | undefined {
  const inbox = findActivityInbox(inboxes, activity);
  const assignee = findActivityAssignee(findTeammate, activity);
  const recipient = findActivityRecipient(findTeammate, activity);

  const sharedProperties = {
    id: activity.id,
    date: activity.date,
    activityType: activity.type,
  };

  /*
   * Priority 1.
   */

  // Conversation assigned to you.
  if (
    activity.basicVisualType === BasicVisualTypesEnum.ASSIGNEE &&
    activity.source.type === ActivitySourceTypesEnum.TEAMMATE &&
    activity.assigneeId === userId
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_USER,
      priority: 1,
      ...sharedProperties,
    };
  }

  // Conversation assigned to you by rule.
  if (
    activity.basicVisualType === BasicVisualTypesEnum.ASSIGNEE &&
    activity.source.type === ActivitySourceTypesEnum.RULE &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.RULE &&
    activity.assigneeId === userId
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_USER_BY_RULE,
      priority: 1,
      ...sharedProperties,
    };
  }

  // Conversation assigned to you by the API.
  if (
    activity.basicVisualType === BasicVisualTypesEnum.ASSIGNEE &&
    activity.source.type === ActivitySourceTypesEnum.PUBLIC_API &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.OTHER &&
    activity.assigneeId === userId
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_USER_BY_API,
      priority: 1,
      ...sharedProperties,
    };
  }

  // Conversation unassigned from you.
  if (
    activity.type === BasicActivityTypesEnum.UNASSIGN &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE &&
    activity.source.teammateId === userId
  ) {
    // Unassigned by current user or current user is OOO.
    return {
      type: VisualBasicCollapsedActivityTypesEnum.UNASSIGNED_FROM_USER,
      priority: 1,
      ...sharedProperties,
    };
  }

  // Conversation snooze expired.
  if (
    activity.type === BasicActivityTypesEnum.REMINDER ||
    activity.type === BasicActivityTypesEnum.TRACKER_REMINDER
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.SNOOZE_EXPIRED,
      priority: 1,
      ...sharedProperties,
    };
  }

  // Rule notified you (with recipient id available).
  if (
    (activity.type === BasicActivityTypesEnum.TRACKER_SHARE ||
      activity.type === BasicActivityTypesEnum.RULE_NOTIFY) &&
    activity.basicVisualType === BasicVisualTypesEnum.TRACKER_SHARE &&
    activity.source.type === ActivitySourceTypesEnum.RULE &&
    (activity.source.visualType === ActivitySourceVisualTypesEnum.RULE ||
      activity.source.visualType === ActivitySourceVisualTypesEnum.OTHER) &&
    activity.recipientId === userId
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.RULE_NOTIFIED_USER_WITH_RECIPIENT,
      priority: 1,
      ...sharedProperties,
    };
  }

  // Rule notified you (without recipient id available).
  if (
    (activity.type === BasicActivityTypesEnum.TRACKER_SHARE ||
      activity.type === BasicActivityTypesEnum.RULE_NOTIFY) &&
    activity.basicVisualType === BasicVisualTypesEnum.PLAIN &&
    activity.source.type === ActivitySourceTypesEnum.RULE &&
    (activity.source.visualType === ActivitySourceVisualTypesEnum.RULE ||
      activity.source.visualType === ActivitySourceVisualTypesEnum.OTHER)
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.RULE_NOTIFIED_USER_WITHOUT_RECIPIENT,
      priority: 1,
      ...sharedProperties,
    };
  }

  // You were invited.
  if (
    activity.type === BasicActivityTypesEnum.TRACKER_SHARE &&
    activity.basicVisualType === BasicVisualTypesEnum.TRACKER_SHARE &&
    activity.source.type === ActivitySourceTypesEnum.TEAMMATE &&
    activity.recipientId === userId
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.USER_INVITED,
      priority: 1,
      ...sharedProperties,
    };
  }

  /*
   * Priority 2.
   */

  // Conversation assigned to a teammate.
  if (
    activity.basicVisualType === BasicVisualTypesEnum.ASSIGNEE &&
    activity.source.type === ActivitySourceTypesEnum.TEAMMATE &&
    activity.assigneeId !== userId &&
    assignee
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_TEAMMATE,
      priority: 2,
      assignee,
      ...sharedProperties,
    };
  }

  // Conversation assigned to teammate by rule.
  if (
    activity.basicVisualType === BasicVisualTypesEnum.ASSIGNEE &&
    activity.source.type === ActivitySourceTypesEnum.RULE &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.RULE &&
    assignee
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_TEAMMATE_BY_RULE,
      priority: 2,
      assignee,
      ...sharedProperties,
    };
  }

  if (
    activity.basicVisualType === BasicVisualTypesEnum.ASSIGNEE &&
    activity.source.type === ActivitySourceTypesEnum.PUBLIC_API &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.OTHER &&
    assignee
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_TEAMMATE_BY_API,
      priority: 2,
      assignee,
      ...sharedProperties,
    };
  }

  // Unassigned by a teammate or teammate is OOO.
  if (
    activity.type === BasicActivityTypesEnum.UNASSIGN &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE &&
    activity.source.teammateId !== userId
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.UNASSIGNED_FROM_TEAMMATE,
      priority: 2,
      ...sharedProperties,
    };
  }

  // Unassigned due to inactivity.
  if (
    activity.type === BasicActivityTypesEnum.UNASSIGN &&
    activity.source.type === ActivitySourceTypesEnum.INACTIVE &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.OTHER
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.UNASSIGNED_FROM_TEAMMATE_INACTIVITY,
      priority: 2,
      ...sharedProperties,
    };
  }

  // Unassigned due to deleted teammate.
  if (
    activity.type === BasicActivityTypesEnum.UNASSIGN &&
    activity.source.type === ActivitySourceTypesEnum.COMPANY &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.OTHER
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.UNASSIGNED_FROM_TEAMMATE_DELETED,
      priority: 2,
      ...sharedProperties,
    };
  }

  // Conversation moved by teammate.
  if (
    activity.basicVisualType === BasicVisualTypesEnum.INBOX &&
    activity.source.type === ActivitySourceTypesEnum.TEAMMATE &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE &&
    inbox
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.MOVED_BY_TEAMMATE,
      priority: 2,
      inbox,
      ...sharedProperties,
    };
  }

  // Conversation moved by rule.
  if (
    activity.basicVisualType === BasicVisualTypesEnum.INBOX &&
    activity.source.type === ActivitySourceTypesEnum.RULE &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.RULE &&
    inbox
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.MOVED_BY_RULE,
      priority: 2,
      inbox,
      ...sharedProperties,
    };
  }

  // Conversation archived in team inbox.
  if (
    activity.type === BasicActivityTypesEnum.ARCHIVE &&
    activity.basicVisualType !== BasicVisualTypesEnum.REMINDER
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.ARCHIVED_IN_TEAM_INBOX,
      priority: 2,
      ...sharedProperties,
    };
  }

  // Conversation snoozed in team inbox.
  if (
    activity.type === BasicActivityTypesEnum.ARCHIVE &&
    activity.basicVisualType === BasicVisualTypesEnum.REMINDER
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.SNOOZED_IN_TEAM_INBOX,
      priority: 2,
      ...sharedProperties,
    };
  }

  // Conversation reopened in team inbox by teammate.
  if (activity.type === BasicActivityTypesEnum.REOPEN) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.REOPENED_IN_TEAM_INBOX,
      priority: 2,
      ...sharedProperties,
    };
  }

  // Conversation trashed in team inbox.
  if (activity.type === BasicActivityTypesEnum.TRASH) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.TRASHED_IN_TEAM_INBOX,
      priority: 2,
      ...sharedProperties,
    };
  }

  // Conversation restored in team inbox.
  if (activity.type === BasicActivityTypesEnum.RESTORE) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.RESTORED_IN_TEAM_INBOX,
      priority: 2,
      ...sharedProperties,
    };
  }

  /*
   * Priority 3.
   */

  // A tag was added by teammate.
  if (
    activity.type === BasicActivityTypesEnum.TAG &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.TAG_ADDED_BY_TEAMMATE,
      priority: 3,
      addedTagCount,
      ...sharedProperties,
    };
  }

  // A tag was added by a rule.
  if (
    activity.type === BasicActivityTypesEnum.TAG &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.RULE
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.TAG_ADDED_BY_RULE,
      priority: 3,
      addedTagCount,
      ...sharedProperties,
    };
  }

  // A tag was removed by a rule.
  if (
    activity.type === BasicActivityTypesEnum.UNTAG &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.RULE
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.TAG_REMOVED_BY_RULE,
      priority: 3,
      removedTagCount,
      ...sharedProperties,
    };
  }

  // A tag was added by the API.
  if (
    activity.type === BasicActivityTypesEnum.TAG &&
    activity.source.type === ActivitySourceTypesEnum.PUBLIC_API &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.OTHER
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.TAG_ADDED_BY_API,
      priority: 3,
      addedTagCount,
      ...sharedProperties,
    };
  }

  // A tag was removed by the API.
  if (
    activity.type === BasicActivityTypesEnum.UNTAG &&
    activity.source.type === ActivitySourceTypesEnum.PUBLIC_API &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.OTHER
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.TAG_REMOVED_BY_API,
      priority: 3,
      removedTagCount,
      ...sharedProperties,
    };
  }

  // A tag was removed by a teammate.
  if (
    activity.type === BasicActivityTypesEnum.UNTAG &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.TAG_REMOVED_BY_TEAMMATE,
      priority: 3,
      removedTagCount,
      ...sharedProperties,
    };
  }

  // A teammate was invited.
  if (
    activity.type === BasicActivityTypesEnum.TRACKER_SHARE &&
    activity.basicVisualType === BasicVisualTypesEnum.TRACKER_SHARE &&
    activity.source.type === ActivitySourceTypesEnum.TEAMMATE &&
    activity.recipientId !== userId &&
    recipient
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.TEAMMATE_INVITED,
      priority: 3,
      recipient,
      ...sharedProperties,
    };
  }

  // A teammate was removed.
  if (activity.type === BasicActivityTypesEnum.TRACKER_UNSHARE && recipient) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.TEAMMATE_REMOVED,
      priority: 3,
      recipient,
      ...sharedProperties,
    };
  }

  // Rule notified a teammate.
  if (
    (activity.type === BasicActivityTypesEnum.TRACKER_SHARE ||
      activity.type === BasicActivityTypesEnum.RULE_NOTIFY) &&
    activity.basicVisualType === BasicVisualTypesEnum.TRACKER_SHARE &&
    activity.source.type === ActivitySourceTypesEnum.RULE &&
    (activity.source.visualType === ActivitySourceVisualTypesEnum.RULE ||
      activity.source.visualType === ActivitySourceVisualTypesEnum.OTHER) &&
    activity.recipientId !== userId &&
    recipient
  ) {
    return {
      type: VisualBasicCollapsedActivityTypesEnum.RULE_NOTIFIED_TEAMMATE,
      priority: 3,
      recipient,
      ...sharedProperties,
    };
  }

  return undefined;
}

function findActivityAssignee(findTeammate: FindTeammate, activity: BasicActivityModel) {
  if (activity.basicVisualType !== BasicVisualTypesEnum.ASSIGNEE) {
    return undefined;
  }

  return findTeammate(activity.assigneeId, 'findActivityAssignee');
}

function findActivityRecipient(findTeammate: FindTeammate, activity: BasicActivityModel) {
  if (
    (activity.basicVisualType !== BasicVisualTypesEnum.TRACKER_SHARE &&
      activity.basicVisualType !== BasicVisualTypesEnum.TRACKER_UNSHARE) ||
    !activity.recipientId
  ) {
    return undefined;
  }

  return findTeammate(activity.recipientId, 'findActivityRecipient');
}

function findActivityInbox(inboxes: ResourceMap<InboxModel>, activity: BasicActivityModel) {
  if (activity.basicVisualType !== BasicVisualTypesEnum.INBOX) {
    return undefined;
  }

  return inboxes[activity.inboxId];
}
